/**
 * @fileoverview English language pack
 * @description Centralized management of all English text content
 */

const en = {
  // Common
  common: {
    appName: 'AugurX',
    appSlogan: 'Professional Commodities Prediction Market',
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    confirm: 'Confirm',
    cancel: 'Cancel',
    submit: 'Submit',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    back: 'Back',
    more: 'More',
    all: 'All',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    yes: 'Yes',
    no: 'No',
    amount: 'Amount',
    price: 'Price',
    volume: 'Volume',
    time: 'Time',
    status: 'Status',
    action: 'Action',
    comingSoon: 'Coming Soon',
    connectWallet: 'Connect Wallet',
    copySuccess: 'Copied!',
    noData: 'No data available',
    refresh: 'Refresh',
    loadMore: 'Load More',
    login: 'Login',
    processing: 'Processing...',
    submitting: 'Submitting...',
    close: 'Close',
    viewAll: 'View All',
    participants: 'participants',
    ended: 'Ended',
    daysLater: 'in {{d}}d',
    hoursLater: 'in {{h}}h',
    minutesLater: 'in {{m}}m',
    daysAgo: '{{d}}d ago',
    hoursAgo: '{{h}}h ago',
    minutesAgo: '{{m}}m ago',
    justNow: 'just now',
    live: 'LIVE',
    hot: 'Hot',
    new: 'New',
    enterAfter: 'Enter',
    marketsCount: '{{n}} Markets',
    foundMarkets: '{{n}} markets found',
    adjustFilter: 'Try adjusting filters or search keywords',
    filterAndSort: 'Filter & Sort',
    recentlyViewed: 'Recently Viewed',
    clearHistory: 'Clear History',
  },

  // Navigation
  nav: {
    home: 'Home',
    markets: 'Markets',
    portfolio: 'Portfolio',
    leaderboard: 'Leaderboard',
    wallet: 'Wallet',
    loginRequired: 'Please login to view your wallet',
    loginNow: 'Login Now',
    depositUsdt: 'Deposit USDT',
    withdrawUsdt: 'Withdraw USDT',
    depositAmount: 'Deposit Amount (USDT)',
    receivingAddress: 'Receiving Address',
    connectWalletFirst: 'Please connect your wallet first',
    metamaskNotFound: 'MetaMask not detected. Please install MetaMask.',
    chainConfigFailed: 'Failed to fetch chain configuration',
    waitingSign: 'Waiting for MetaMask signature...',
    waitingConfirm: 'Waiting for on-chain confirmation...',
    viewTx: 'View Transaction',
    depositConfirmed: 'Deposit confirmed. Balance will update in 1-5 minutes.',
    depositBroadcast: 'Transaction broadcast. Deposit will be credited after confirmation.',
    depositConfirmedMsg: 'Deposit confirmed. Balance will update in 1-5 minutes.',
    depositWarning: 'Ensure MetaMask is switched to BSC Mainnet (BNB Smart Chain). USDT (BEP-20) will be transferred directly from your wallet to the platform.',
    userRejectedSign: 'Cancelled: User rejected wallet signature',
    tonWallet: 'TON Wallet',
    connectTonWallet: 'Connect TON Wallet',
    tonDepositWarning: 'Please send USDT Jetton via TON Wallet. The system will automatically credit your balance.',
    depositFailed: 'Deposit failed',
    guidError: 'Unable to get user GUID, please log in again',
    jettonWalletError: 'Unable to get Jetton Wallet address',
    jettonWalletFetchError: 'Failed to get TON Jetton Wallet address, please try again',
    confirmDeposit: 'Confirm Deposit',
    minDepositError: 'Minimum deposit is 10 USDT',
    depositMinHint: 'Min {{min}} USDT',
    withdrawAmount: 'Withdraw Amount',
    available: 'Available',
    fee: 'Fee',
    actualAmount: 'You Receive',
    confirmWithdraw: 'Confirm Withdrawal',
    invalidWithdrawAmount: 'Please enter a valid withdrawal amount',
    insufficientBalance: 'Withdrawal amount exceeds available balance',
    withdrawSubmitted: 'Withdrawal submitted. Expected within 24 hours.',
    withdrawFailed: 'Withdrawal failed, error code: {{code}}',
    withdrawRequestFailed: 'Withdrawal request failed',
    noDeposits: 'No deposit records',
    noWithdrawals: 'No withdrawal records',
    statusReviewing: 'Reviewing',
    statusTransfering: 'Transferring',
    statusApproved: 'Approved',
    statusCompleted: 'Completed',
    statusUnknown: 'Unknown',
    dataRefreshed: 'Data refreshed',
    balanceUpdated: 'Balance updated',
    profile: 'Profile',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    telegram: 'Telegram',
    langZh: 'ZH',
    langEn: 'EN',
    langZhFull: '繁體中文',
    categories: {
      all: 'All Markets',
      crypto: 'Crypto',
      precious_metals: 'Precious Metals',
      energy: 'Energy',
      chemicals: 'Chemicals',
      agriculture: 'Agriculture',
    },
  },

  // Home
  home: {
    hero: {
      badge: 'Commodity Prediction Market · Fixed Settlement',
      title: 'Read the Market, Win Before the Bell',
      titleLine1: 'Read the Market,',
      titleLine2: 'Win Before the Bell',
      subtitle: 'Participate in commodity & crypto price predictions and compete with global traders',
      cta: 'Start Predicting',
      ctaLeaderboard: 'View Leaderboard',
      leaderboardCta: 'View Leaderboard',
      stats: {
        totalVolume: 'Total Volume',
        activeMarkets: 'Active Markets',
        totalUsers: 'Registered Users',
      },
    },
    ticker: {
      label: 'Live Prices',
    },
    highFreq: {
      badge: 'High-Freq',
      title: '5-Min Speed Prediction',
      subtitle: 'Predict price movement in 5 minutes based on real-time price',
      tagline: 'Auto-created every 5 min · Based on real-time price',
      cta: 'Join Now',
      label: 'High-Freq Market',
      basePrice: 'Base Price',
      currentPrice: 'Current',
      fetching: 'Fetching...',
      bannerTitle: '5-Min Speed Prediction',
      bannerDesc: 'Predict UP or DOWN in 5 minutes based on real-time price',
      joinNow: 'Join Now',
      desc: 'Auto-created every 5 min · Based on real-time price',
    },
    categoryBanner: {
      commodityTitle: 'Commodity Prediction Market',
      marketsCount: 'markets',
      enter: 'Enter',
    },
    sections: {
      hot: 'Hot Markets',
      new: 'Newly Listed',
      crypto: 'Crypto',
      commodities: 'Commodities',
      commodity: 'Commodities',
      highFreq: 'High-Freq Markets',
      commodityPrediction: 'Commodity Prediction',
      priceTrend: 'Price Trend Markets',
      hotEvent: 'Hot Event Markets',
      recentViewed: 'Recently Viewed',
      closedMarkets: 'Closed Markets',
    },
    viewAll: 'View All',
  },

  // Market
  market: {
    long: 'Long',
    short: 'Short',
    status: {
      active: 'Active',
      resolved: 'Resolved',
      cancelled: 'Cancelled',
      pending: 'Pending',
      ended: 'Ended',
      closed: 'Closed',
    },
    category: {
      all: 'All',
      high_freq: 'High-Freq',
      crypto: 'Crypto',
      commodity: 'Commodity',
      futures: 'Futures',
      forex: 'Forex',
      stock: 'Stock',
      index: 'Index',
      event: 'Event',
      precious_metals: 'Precious Metals',
      energy: 'Energy',
      chemicals: 'Chemicals',
      agriculture: 'Agriculture',
      minerals: 'Minerals',
    },
    detail: {
      endTime: 'End Time',
      totalVolume: 'Total Volume',
      liquidity: 'Liquidity',
      participants: 'Participants',
      priceHistory: 'Price History',
      orderBook: 'Order Book',
      myPosition: 'My Position',
      rules: 'Rules',
      asset: 'Asset',
      basePrice: 'Base Price (at creation)',
      targetPriceLabel: 'Target Price',
      currentPrice: 'Current Price',
      fetching: 'Fetching...',
      notSet: 'Not set',
      highFreqBadge: 'High-Freq',
      yesLabel: 'YES',
      noLabel: 'NO',
      upLabel: 'UP',
      downLabel: 'DOWN',
      longLabel: 'Long',
      shortLabel: 'Short',
      totalVolumeStat: 'Total Volume',
      endTimeStat: 'Ends',
      endsAt: 'Ends {{t}}',
      opensAt: 'Opens {{t}}',
      participantsStat: 'Participants',
      liveLabel: 'LIVE',
      hotBadge: 'Hot',
      rulesTitle: 'Market Rules',
      endTimeLabel: 'End Time: ',
      nextOpenTimeLabel: 'Opens at: ',
      sessionEndTimeLabel: 'Closes at: ',
      closedUntil: 'Market closed, opens at ',
      settlementLabel: 'Settlement: ',
      settlementDesc: 'Fixed-time settlement, all participants share the prize pool',
      marketNotFound: 'Market not found or has been removed',
      backToMarkets: 'Back to Markets',
      currentShort: 'Current',
    },
    bet: {
      title: 'Place Prediction',
      buyYes: 'Buy YES',
      buyNo: 'Buy NO',
      sellYes: 'Sell YES',
      sellNo: 'Sell NO',
      amount: 'Amount',
      maxAmount: 'Max Amount',
      estimatedReturn: 'Est. Return',
      potentialProfit: 'Potential Profit',
      confirm: 'Confirm',
      balance: 'Available Balance',
      minAmount: 'Min Amount',
      odds: 'Multiplier',
      returnRate: 'Return Rate',
      returnRateEst: 'Est. Return Rate',
      returnRateTitle: 'My Return Rate Trend',
      returnRateTitleMarket: 'Market Return Rate Trend',
      returnRateInfo: 'Return rate = your PROFIT if you win ÷ your stake (principal excluded). e.g. 100% means you net one extra stake (bet 100, get 200 back). Profit comes from the losing side, so it is lower when the opposite pool is small.\nOptions you have NOT bet show the "market" trend (the rate for entering now). Options you HAVE bet switch to YOUR own weight (locked by when you entered, earlier = higher), then move only as others bet — so everyone differs.',
      returnRateEmpty: 'Collecting return-rate data…',
      myPositionTitle: 'My Position (Est.)',
      posStake: 'Staked',
      yesDirection: 'YES',
      noDirection: 'NO',
      loginToBet: 'to join prediction',
      suspended: 'Market Suspended',
      suspendedDesc: 'Market is in off-hours period, will resume automatically at open',
      notStarted: 'Market Not Open Yet',
      notStartedTime: 'Opens at: ',
      notStartedCountdown: 'in {{t}}',
      lockWindow: 'Prediction Closed',
      lockWindowDesc: 'Settling, waiting for next round...',
      successToast: 'Joined ${{amount}} on "{{side}}" successfully',
      myOrders: 'My Predictions',
      noOrders: 'No prediction records',
      buyYesLabel: 'Buy YES',
      buyNoLabel: 'Buy NO',
      won: 'Won',
      lost: 'Lost',
      pending: 'Pending',
      loadMoreOrders: 'Load More ({{n}} remaining)',
      roundCountdown: 'Round Countdown',
      roundEnded: 'Round ended, waiting for next round...',
      roundLabel: 'Round #{{n}}',
      roundResult: 'Result: {{side}} wins',
      roundWon: 'Won',
      roundLost: 'Lost',
      roundTie: 'Tie',
      roundPending: 'Pending',
      roundYesTotal: 'YES Total',
      roundNoTotal: 'NO Total',
      roundProfit: 'Profit',
      roundGrossProfit: 'Gross Profit',
      roundTax: 'Platform Fee',
      roundNetProfit: 'Round Net P&L',
      roundWinProfit: 'Winning Profit',
      roundLossAmount: 'Losing Side Loss',
      roundBasePrice: 'Base Price',
      roundSettlePrice: 'Settle Price',
      roundPriceUnsettled: 'Pending',
      betAmountTooLow: 'Bet amount must be at least {{min}} USDT',
      betAmountTooHigh: 'Bet amount cannot exceed {{max}} USDT',
    },
    betStatus: {
      preparingChain: 'Insufficient balance, preparing on-chain transfer...',
      waitingSign: 'Please confirm the transfer signature in your wallet...',
      waitingConfirm: 'Transaction broadcast, waiting for on-chain confirmation...',
      chainSuccess: 'On-chain transfer successful!',
      chainPending: 'Waiting for backend confirmation, balance will update shortly',
      chainError: 'On-chain transfer failed, please retry',
      submitting: 'Submitting prediction...',
      fetchingConfig: 'Fetching chain config...',
      waitingSignBtn: 'Waiting for wallet signature...',
      waitingConfirmBtn: 'Waiting for confirmation...',
      processingBtn: 'Processing...',
    },
    settlement: {
      settled: 'Market Settled',
      expired: 'Market Expired',
      result: 'Result: {{side}} wins',
      waiting: 'Awaiting settlement...',
      won: 'Winner',
      yesLabel: 'YES',
      noLabel: 'NO',
      condition: 'Market Condition',
      conditionDesc: 'Price {{type}} ${{price}} at expiry',
      priceBelow: 'below',
      priceEqual: 'equal to',
      priceAbove: 'above',
      endTime: 'End Time',
      settledToast: 'Market settled! Result: {{side}} wins',
      settledToastDesc: 'Order list has been updated, check your latest P&L.',
    },
    sort: {
      volume: 'By Volume',
      endTime: 'By End Time',
      newest: 'Newest',
      liquidity: 'By Liquidity',
    },
    filter: {
      status: 'Status',
      category: 'Category',
      endTime: 'End Time',
    },
    markets: {
      badge: 'Commodity Prediction Market',
      subtitle: 'Participate in commodity price predictions with fixed settlement time',
      activeMarkets: 'Active Markets',
      totalMarkets: 'Total Markets',
      yesPercent: 'YES {{n}}%',
      noPercent: 'NO {{n}}%',
      active: 'Active',
      ended: 'Ended',
    },
    card: {
      hot: 'Hot',
      up: 'UP',
      down: 'DOWN',
      countdown: 'Closes in',
      participants: 'Participants',
      expired: 'Expired',
      daysLater: 'in {{n}}d',
      hoursLater: 'in {{n}}h',
      closed: 'Closed',
      unitDay: 'd',
      unitHour: 'h',
      unitMin: 'm',
      unitSec: 's',
    },
  },

  // Markets page
  markets: {
    recentViewed: 'Recently Viewed',
    clearHistory: 'Clear History',
    subtitle: 'Participate in commodity price predictions with fixed settlement time',
    activeMarkets: 'Active Markets',
    totalMarkets: 'Total Markets',
    filterAndSort: 'Filter & Sort',
    emptyDesc: 'Try adjusting filters or search keywords',
    resultCount: '{{n}} markets found',
  },

  // Portfolio
  portfolio: {
    title: 'My Portfolio',
    totalValue: 'Total Value',
    unrealizedPnl: 'Unrealized PnL',
    realizedPnl: 'Realized PnL',
    positions: 'Open Positions',
    history: 'Order History',
    noPositions: 'No prediction records',
    loginRequired: 'Please login to view your portfolio',
    goToMarkets: 'Go to Markets to join predictions',
    profit: 'Profit',
    loss: 'Loss',
    amount: 'Amount',
    claimSuccess: 'Reward claimed successfully!',
    claimFailed: 'Claim failed, error code: {{code}}',
    activeLabel: 'Active',
    statusWon: 'Won',
    statusLost: 'Lost',
    statusActive: 'Active',
    settledLabel: 'Settled',
    netProfit: 'Net Profit',
    accountBalance: 'Balance',
    activeTab: 'Active',
    historyTab: 'History',
    columns: {
      market: 'Market',
      side: 'Side',
      amount: 'Amount',
      avgPrice: 'Avg Price',
      currentPrice: 'Current Price',
      pnl: 'PnL',
      action: 'Action',
    },
  },

  // Wallet
  wallet: {
    title: 'My Wallet',
    balance: 'Available Balance',
    totalAssets: 'Total Assets',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    transfer: 'Transfer',
    history: 'Transaction History',
    totalBalance: 'Total Balance',
    available: 'Available',
    frozen: 'Frozen',
    totalDeposit: 'Total Deposited',
    totalWithdraw: 'Total Withdrawn',
    loginRequired: 'Please login to view your wallet',
    loginNow: 'Login Now',
    refreshed: 'Data refreshed',
    txHistory: {
      all: 'All',
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      bet: 'Prediction',
    },
    depositModal: {
      title: 'Deposit',
      network: 'Select Network',
      address: 'Deposit Address',
      minAmount: 'Min Deposit Amount',
      tips: 'Deposit Notes',
      confirmed: 'Deposit confirmed, balance will update in 1-5 minutes',
      warning: 'Please ensure MetaMask is switched to BSC Mainnet (BNB Smart Chain). USDT (BEP-20) will be transferred directly from your wallet to the platform.',
      confirm: 'Confirm Deposit',
      close: 'Close',
      betDeposit: 'Prediction Deposit',
      depositUsdt: 'Deposit USDT',
      noDeposits: 'No deposit records',
    },
    withdrawModal: {
      title: 'Withdraw USDT',
      address: 'Withdrawal Address',
      amount: 'Withdrawal Amount',
      amountLabel: 'Withdrawal Amount',
      available: 'Available: ',
      minHint: 'Min 20 USDT',
      all: 'All',
      fee: 'Fee',
      actualAmount: 'Actual Amount',
      confirm: 'Confirm Withdrawal',
      invalidAmount: 'Please enter a valid withdrawal amount',
      exceedBalance: 'Amount exceeds available balance',
      submitted: 'Withdrawal submitted, expected within 24 hours',
      failed: 'Withdrawal failed, error code: {{code}}',
      error: 'Withdrawal request failed',
      noWithdraws: 'No withdrawal records',
    },
    transactionType: {
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      win: 'Prediction Win',
      loss: 'Prediction Loss',
      fee: 'Fee',
    },
    status: {
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      reviewing: 'Reviewing',
      approved: 'Approved',
      done: 'Done',
      unknown: 'Unknown',
    },
    withdrawStatus: {
      0: 'Reviewing',
      1: 'Approved',
      2: 'Completed',
    },
    loadingDeposit: 'Loading...',
    loadingWithdraw: 'Loading...',
    depositUsdt: 'Deposit USDT',
    withdrawUsdt: 'Withdraw USDT',
    depositAmount: 'Deposit Amount (USDT)',
    receivingAddress: 'Receiving Address',
    connectWalletFirst: 'Please connect your wallet first',
    metamaskNotFound: 'MetaMask not detected. Please install MetaMask.',
    chainConfigFailed: 'Failed to fetch chain configuration',
    waitingSign: 'Waiting for MetaMask signature...',
    waitingConfirm: 'Waiting for on-chain confirmation...',
    viewTx: 'View Transaction',
    depositConfirmed: 'Deposit confirmed. Balance will update in 1-5 minutes.',
    depositBroadcast: 'Transaction broadcast. Deposit will be credited after confirmation.',
    depositConfirmedMsg: 'Deposit confirmed. Balance will update in 1-5 minutes.',
    depositWarning: 'Ensure MetaMask is switched to BSC Mainnet (BNB Smart Chain). USDT (BEP-20) will be transferred directly from your wallet to the platform.',
    userRejectedSign: 'Cancelled: User rejected wallet signature',
    tonWallet: 'TON Wallet',
    connectTonWallet: 'Connect TON Wallet',
    tonDepositWarning: 'Please send USDT Jetton via TON Wallet. The system will automatically credit your balance.',
    depositFailed: 'Deposit failed',
    guidError: 'Unable to get user GUID, please log in again',
    jettonWalletError: 'Unable to get Jetton Wallet address',
    jettonWalletFetchError: 'Failed to get TON Jetton Wallet address, please try again',
    confirmDeposit: 'Confirm Deposit',
    minDepositError: 'Minimum deposit is 10 USDT',
    depositMinHint: 'Min {{min}} USDT',
    withdrawAmount: 'Withdraw Amount',
    selectChain: 'Select Chain',
    withdrawAddress: 'Withdrawal Address',
    withdrawAddressPlaceholder: 'Enter receiving wallet address (e.g. 0x...)',
    withdrawAddressPlaceholderTon: 'TON address in UQ... or EQ... format',
    withdrawAddressPlaceholderEvm: 'EVM address in 0x... format',
    withdrawAddressRequired: 'Please enter a withdrawal wallet address',
    withdrawMinHint: 'Withdrawal amount must be at least {{min}} USDT',
    fee: 'Fee',
    actualAmount: 'You Receive',
    confirmWithdraw: 'Confirm Withdrawal',
    invalidWithdrawAmount: 'Please enter a valid withdrawal amount',
    insufficientBalance: 'Withdrawal amount exceeds available balance',
    withdrawSubmitted: 'Withdrawal submitted. Expected within 24 hours.',
    withdrawFailed: 'Withdrawal failed, error code: {{code}}',
    withdrawRequestFailed: 'Withdrawal request failed',
    noDeposits: 'No deposit records',
    noWithdrawals: 'No withdrawal records',
    statusReviewing: 'Reviewing',
    statusTransfering: 'Transferring',
    statusApproved: 'Approved',
    statusCompleted: 'Completed',
    statusUnknown: 'Unknown',
    dataRefreshed: 'Data refreshed',
    balanceUpdated: 'Balance updated',
  },

  // Leaderboard
  leaderboard: {
    title: 'Profit Leaderboard',
    subtitle: 'Top predictors by cumulative profit',
    listTitle: 'Profit Leaderboard',
    period: {
      daily: 'Today',
      weekly: 'This Week',
      monthly: 'This Month',
      all: 'All Time',
    },
    columns: {
      rank: 'Rank',
      user: 'User',
      pnl: 'PnL',
      pnlPercent: 'Return',
      volume: 'Volume',
      winRate: 'Win Rate',
      markets: 'Markets',
      profit: 'Profit',
    },
    smartMoney: 'Smart Money',
    profitLabel: 'Cumulative Profit',
    loading: 'Loading...',
    noData: 'No leaderboard data yet',
    champion: 'Champion',
    runnerUp: 'Runner-up',
    thirdPlace: '3rd Place',
    gold: 'Champion',
    silver: 'Runner-up',
    bronze: '3rd Place',
    me: 'me',
    user: 'User',
    totalProfit: 'Cumulative Profit',
    empty: 'No leaderboard data yet',
    myRank: 'You are ranked #{{rank}} · Profit +{{profit}}',
    notRanked: 'Not ranked yet · Keep predicting to climb the board',
  },

  // Profile
  profile: {
    title: 'Profile',
    editProfile: 'Edit Profile',
    settings: 'Account Settings',
    security: 'Security',
    notifications: 'Notifications',
    telegram: 'Telegram Binding',
    language: 'Language',
    theme: 'Theme',
    about: 'About Us',
    preferences: 'Preferences',
    loginRequired: 'Please login to view your profile',
    loginNow: 'Login Now',
    logoutSuccess: 'Logged out successfully',
    telegramBound: 'Telegram Bound',
    telegramNotBound: 'Not Bound',
    languageCurrent: 'English',
    stats: {
      totalPnl: 'Total PnL',
      winRate: 'Win Rate',
      totalMarkets: 'Markets Joined',
      totalVolume: 'Total Volume',
    },
    rewards: {
      title: 'Complete Profile for Points',
      completed: '{{n}}/{{total}} completed',
      pointsLabel: 'Points Earned',
      tip: 'Points can be redeemed for fee discounts, token airdrop eligibility, and exclusive perks',
      bindEmail: 'Bind Email',
      bindPhone: 'Bind Phone',
      bindTelegram: 'Bind Telegram',
      kyc: 'Complete KYC',
      done: 'Done',
    },
    securityTip: {
      web3Tip: 'Web3 Security Reminder',
      web3TipDesc: 'Keep your wallet private key and seed phrase safe. AugurX never stores your private key and official staff will never ask for sensitive information.',
    },
  },

  // Auth
  auth: {
    login: {
      title: 'Welcome Back',
      subtitle: 'Connect your wallet to start predicting',
      email: 'Email Address',
      password: 'Password',
      forgotPassword: 'Forgot Password?',
      submit: 'Sign In',
      noAccount: "Don't have an account?",
      register: 'Register Now',
      orContinueWith: 'Or sign in with email',
      connectWallet: 'Connect Wallet',
      walletSubtitle: 'No registration needed, connect wallet to use anonymously',
      metamask: 'MetaMask',
      walletconnect: 'WalletConnect',
      okx: 'OKX Wallet',
      connectAndPredict: 'Connect & Start Predicting',
      connectDesc: 'Choose your login method, no registration required',
      connectWalletLabel: 'Connect Wallet',
      or: 'or',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      agreePrefix: 'By connecting you agree to our',
      agreeMid: 'and',
      termsComingSoon: 'Terms of Service coming soon',
      privacyComingSoon: 'Privacy Policy coming soon',
      telegramComingSoon: 'Telegram login is coming soon',
      telegramBotOnly: 'Please open this app via Telegram Bot to use Telegram login',
      telegramDesc: 'Authorize via Telegram Bot',
      recommended: 'Recommended',
      walletConnecting: 'Connecting wallet...',
      walletSigning: 'Please sign in your wallet...',
      walletLoggingIn: 'Verifying login...',
      walletConnecting2: 'Connecting...',
      connectSuccess: 'Wallet connected! Welcome to AugurX',
      connectFailed: 'Wallet connection failed, please retry',
      connectError: 'Connection failed: {{msg}}',
      brandTitle: 'Predict Commodities\nSeize Market Edge',
      brandDesc: 'Connect wallet or Telegram, no registration, anonymous access to global prediction markets',
      feature1: 'High-Freq: 5-min speed prediction',
      feature2: 'Precious metals, energy, chemicals covered',
      feature3: 'Global leaderboard, smart money tracking',
      walletDesc: {
        metamask: 'Most popular Ethereum wallet',
        walletconnect: 'Supports 300+ mobile wallets',
        okx: 'Official OKX wallet',
      },
    },
    register: {
      title: 'Create Account',
      subtitle: 'Join AugurX Prediction Market',
      username: 'Username',
      email: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      inviteCode: 'Invite Code (Optional)',
      agreeTerms: 'I have read and agree to the',
      terms: 'Terms of Service',
      and: 'and',
      privacy: 'Privacy Policy',
      submit: 'Register',
      hasAccount: 'Already have an account?',
      login: 'Sign In',
    },
    forgotPassword: {
      title: 'Forgot Password',
      subtitle: 'Enter your email and we will send a reset link',
      email: 'Email Address',
      submit: 'Send Reset Email',
      backToLogin: 'Back to Login',
    },
  },

  // Telegram
  telegram: {
    title: 'Bind Telegram',
    subtitle: 'Connect your Telegram account to receive market alerts anytime',
    features: {
      notifications: 'Real-time Market Alerts',
      trading: 'Quick Bot Prediction',
      results: 'Settlement Notifications',
    },
    bindButton: 'Bind Now',
    bound: 'Bound',
    unbind: 'Unbind',
    botName: '@AugurXBot',
    steps: {
      step1: 'Click the button below to open Telegram',
      step2: 'Send /start command to the Bot',
      step3: 'Copy the verification code and enter it here',
    },
    verifyCode: 'Verification Code',
    verify: 'Verify',
  },

  // Footer
  footer: {
    copyright: '© 2025 AugurX. Professional commodity & crypto prediction market.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    help: 'Help Center',
  },
  // Chart
  chart: {
    loading: 'Loading chart data...',
    noData: 'No chart data',
    noDataDesc: 'Data syncing, please refresh the page in a moment',
    myBet: 'My Bet',
  },
  // 404
  notFound: {
    title: 'Page Not Found',
    desc: 'The page you visited does not exist or has been removed',
    backHome: 'Back to Home',
  },

  // Server Status Guard
  serverStatus: {
    connectionFailed: 'Connection Failed',
    connectionFailedDesc: 'Unable to reach the server. Please check your network connection and try again.',
    maintenance: 'Server Under Maintenance',
    maintenanceDesc: 'The system is undergoing maintenance. We will be back shortly.\nThank you for your patience.',
    retry: 'Retry',
    retrying: 'Reconnecting...',
    autoCheck: 'System checks every 10 seconds automatically',
    maintenanceMode: 'Maintenance Mode',
    unreachable: 'Service Unreachable',
  },
  // Token Expired
  tokenExpired: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    action: 'Log In Again',
  },

  // Market card badge
  marketBadge: {
    participating: 'Participating',
  },
  // Invite
  invite: {
    navLabel: 'Invite',
    title: 'Invite Friends',
    subtitle: 'Invite friends to join AugurX and earn points together',
    yourCode: 'Your Invite Code',
    copyCode: 'Copy Code',
    copyLink: 'Copy Link',
    shareOnTelegram: 'Share on Telegram',
    codeCopied: 'Invite code copied!',
    linkCopied: 'Invite link copied!',
    stats: {
      invited: 'Friends Invited',
      pointsEarned: 'Points from Referrals',
      totalPoints: 'Your Total Points',
    },
    howItWorks: 'How It Works',
    step1Title: 'Share Your Link',
    step1Desc: 'Share your unique invite link or code with friends via Telegram or any channel.',
    step2Title: 'Friends Join',
    step2Desc: 'Your friends sign up using your invite link and start predicting on AugurX.',
    step3Title: 'Earn Together',
    step3Desc: "You earn 10% of your friends' prediction points as a referral bonus, forever.",
    rewardRule: 'For every prediction your invited friend makes, you earn 10% of their points as a bonus.',
    history: 'Referral Reward History',
    historyEmpty: 'No referral rewards yet. Invite friends to start earning!',
    historyAccount: 'Friend',
    historyPoints: 'Points Earned',
    historyTime: 'Time',
    loginRequired: 'Please login to view your invite info',
    loginNow: 'Login Now',
    loadMore: 'Load More',
  },
  // About
  about: {
    title: 'About AugurX',
    subtitle: 'The decentralized prediction market for commodities & crypto',
    hero: {
      headline: 'Predict. Earn. Win.',
      desc: 'AugurX is a high-frequency prediction market platform built on TON and EVM chains, focusing on commodities and crypto assets. Predict market movements and earn rewards.',
    },
    features: {
      title: 'Why AugurX?',
      highFreq: {
        title: 'High-Frequency Predictions',
        desc: '5-minute prediction rounds on gold, oil, BTC, ETH and more. Fast, exciting, and rewarding.',
      },
      multiChain: {
        title: 'Multi-Chain Support',
        desc: 'Seamlessly participate via TON wallet (Telegram) or EVM wallets (MetaMask, OKX, WalletConnect).',
      },
      transparent: {
        title: 'Transparent & Fair',
        desc: 'All predictions and settlements are recorded on-chain. No manipulation, no hidden fees.',
      },
      points: {
        title: 'Points & Rewards',
        desc: 'Earn points for every prediction. Redeem for fee discounts, token airdrops, and exclusive perks.',
      },
    },
    howToPlay: {
      title: 'How to Play',
      step1: 'Connect your TON or EVM wallet',
      step2: 'Deposit USDT to your account',
      step3: 'Choose a market and predict UP or DOWN',
      step4: 'Win and claim your rewards',
    },
    points: {
      title: 'Points System',
      desc: 'Earn points through predictions and referrals. Points unlock exclusive benefits.',
      predict: 'Predict & earn 1 point per 1 USDT predicted',
      refer: 'Refer friends & earn 10% of their prediction points',
      bindEmail: 'Bind email for +50 bonus points',
      bindPhone: 'Bind phone for +30 bonus points',
      bindTelegram: 'Bind Telegram for +80 bonus points',
      redeem: 'Points can be redeemed for fee discounts, token airdrops, and exclusive perks',
    },
    community: {
      title: 'Join Our Community',
      telegram: 'Telegram Bot',
      twitter: 'Follow on X (Twitter)',
    },
    contact: {
      title: 'Contact Us',
      desc: 'Have questions or feedback? Reach us through our official channels.',
    },
  },
  // Errors
  errors: {
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordMismatch: 'Passwords do not match',
    networkError: 'Network connection failed, please try again',
    unauthorized: 'Please sign in first',
    notFound: 'Page not found',
  },
};

export default en;
